import type {
    BackendModule,
    InitOptions,
    ReadCallback,
    ResourceKey,
    ResourceStore,
    Services,
} from 'i18next';
import { getTranslationGroup } from './translationGroups';

export type TranslationGroupResources = Record<string, ResourceKey>;
export type TranslationGroupLoader = () => Promise<TranslationGroupResources>;
export type TranslationGroupLoaders = Record<string, Record<string, TranslationGroupLoader>>;

interface WritableResourceStore extends ResourceStore {
    addResourceBundle(
        language: string,
        namespace: string,
        resource: ResourceKey,
        deep?: boolean,
        overwrite?: boolean,
        options?: { silent?: boolean },
    ): void;
}

export const createTranslationBackend = (groupLoaders: TranslationGroupLoaders): BackendModule => {
    const groupResourcesCache = new Map<string, Promise<TranslationGroupResources>>();
    let resourceStore: WritableResourceStore;

    const loadGroup = (language: string, namespace: string) => {
        const group = getTranslationGroup(namespace);
        const cacheKey = `${language}:${group}`;
        const cachedResources = groupResourcesCache.get(cacheKey);
        if (cachedResources) {
            return cachedResources;
        }

        const pendingResources = Promise.resolve()
            .then(() => groupLoaders[language]?.[group]?.() ?? {})
            .then((resources) => {
                for (const [loadedNamespace, resource] of Object.entries(resources)) {
                    resourceStore.addResourceBundle(
                        language,
                        loadedNamespace,
                        resource,
                        true,
                        true,
                        {
                            silent: true,
                        },
                    );
                }
                return resources;
            })
            .catch((error: unknown) => {
                groupResourcesCache.delete(cacheKey);
                throw error;
            });

        groupResourcesCache.set(cacheKey, pendingResources);
        return pendingResources;
    };

    return {
        type: 'backend',
        init(services: Services, _backendOptions: object, _i18nextOptions: InitOptions) {
            resourceStore = services.resourceStore as WritableResourceStore;
        },
        read(language: string, namespace: string, callback: ReadCallback) {
            void loadGroup(language, namespace)
                .then((resources) => callback(null, resources[namespace] ?? {}))
                .catch((error: unknown) =>
                    callback(error instanceof Error ? error : new Error(String(error)), false),
                );
        },
    };
};
